import React, { useEffect, useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import { Button, FormControlLabel, Typography } from "@mui/material";
import { useFormik, setNestedObjectValues } from "formik";
import { useMutation, useQuery } from "@apollo/client";
import { isEqual, omit } from "lodash";
import { useAuth } from "@frontegg/react";
import { GET_USER, ADD_USER } from "../../../graphql/user";
import PlatformDisplayUserInformation from "./PlatformDisplayUserInformation";
import PlatformUserInformation from "./PlatformUserInformation";
import { Checkbox, ConfirmDialog, Drawer, toast } from "@outpave/ds";
import { Stack } from "components/layout/reusableLayouts";
import StatusType from "../../../enums/UserStatus";
import { FormikAddUser } from "hooks/useUserProfileData";
import { generatePlatformUserValidationSchema } from "../../../validations/users";
import useCustomFieldRenderer from "hooks/useCustomFieldRenderer";
import CustomFieldParentType from "enums/CustomFieldParentType";
import { useLogoForScheme } from "utils/themeContextHelper";
import { Account } from "components/common/AccountSearchAutoComplete";

interface AddNewUserDrawerProps {
  open: boolean;
  handleCloseDrawer: () => void;
  handleCreatedUser: (error?: string | null, user?: FormikAddUser) => void;
  selectedCompany?: Pick<Account, "id" | "businessLegalName"> | null;
}

const PlatformAddNewUserDrawer: React.FC<AddNewUserDrawerProps> = (props) => {
  const { handleCreatedUser, open, handleCloseDrawer, selectedCompany } = props;

  const logo = useLogoForScheme();
  const { user } = useAuth();

  const [step, setStep] = useState<number>(0);
  const [openCancelUserCreation, setOpenCancelUserCreation] =
    useState<boolean>(false);

  const {
    customFieldSettings,
    customFieldsInitialValues,
    transformCustomFieldsToArray,
  } = useCustomFieldRenderer({
    parentType: CustomFieldParentType.USER,
  });

  const validationSchema =
    generatePlatformUserValidationSchema(customFieldSettings);

  const startCompany =
    selectedCompany && selectedCompany.id !== "showAll"
      ? selectedCompany
      : undefined;

  const formikAddUser = useFormik<FormikAddUser>({
    initialValues: {
      id: "",
      firstName: "",
      lastName: "",
      email: "",
      employeeGroup: "",
      phone: "",
      status: StatusType.PENDING,
      address: {
        addressName: "",
        address1: "",
        address2: "",
        city: "",
        state: "",
        zipcode: "",
        country: "US",
      },
      location: {
        id: "",
        addressName: "",
      },
      assignedManager: {
        id: "",
        firstName: "",
        lastName: "",
        email: "",
      },
      userPassword: "",
      role: "",
      sendOneTimePassword: true,
      createAndAssignNewCard: false,
      birthdate: null,
      customFields: customFieldsInitialValues,
      company: {
        id: "",
        businessLegalName: "",
      },
    },
    validationSchema: validationSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      const {
        firstName,
        lastName,
        role,
        email,
        status,
        sendOneTimePassword,
        customFields,
        companyId,
      } = values;

      addUser({
        variables: {
          input: {
            companyId: companyId || userData?.user?.company?.id,
            departmentId: userData?.user?.department?.id,
            firstName: firstName,
            middleName: "",
            lastName: lastName,
            title: "Other",
            role: role,
            isOwner: false,
            email: email,
            status: status,
            sendOneTimePassword: sendOneTimePassword,
            customFields: transformCustomFieldsToArray(customFields),
          },
        },
      });
    },
  });

  // The company comes from the page, which can still be loading it, so it is filled in rather than started with:
  // starting values that change re-initialize the form and wipe what was typed.
  const fillCompany = () => {
    if (!startCompany) return;
    formikAddUser.setFieldValue("companyId", startCompany.id);
    formikAddUser.setFieldValue("company", startCompany);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(fillCompany, [selectedCompany]);

  const startOver = () => {
    formikAddUser.resetForm();
    fillCompany();
  };

  const { data: userData } = useQuery(GET_USER, {
    variables: { userId: user?.id },
    skip: Boolean(!user?.id),
  });

  const [addUser, { loading }] = useMutation(ADD_USER, {
    onCompleted: ({ addUser }) => {
      setStep(0);

      const updatedUser = {
        ...formikAddUser.values,
        id: addUser.id,
      };

      if (formikAddUser.values.createAndAssignNewCard) {
        formikAddUser.setValues(updatedUser);
      } else {
        startOver();
        handleCreatedUser(null, updatedUser);
        handleCloseDrawer();
      }
      toast.success(t("User created successfully!"));
    },
    onError: (e) => {
      handleCreatedUser(e.message);
      toast.error(t("Failed to create user") + ": " + e.message);
    },
  });

  const { t } = useTranslation();

  const handleCloseCancelUserCreationModal = () => {
    setOpenCancelUserCreation(false);
  };

  const handleReset = () => {
    setStep(0);
    startOver();
    handleCloseDrawer();
  };

  // A company the page filled in is not an entry; one picked here is.
  const userEntered = (values: FormikAddUser) =>
    omit(values, ["companyId", "company"]);
  const changed =
    !isEqual(
      userEntered(formikAddUser.values),
      userEntered(formikAddUser.initialValues),
    ) || (formikAddUser.values.company?.id || "") !== (startCompany?.id || "");

  const handleClose = () => {
    if (loading) return;
    if (changed) {
      setOpenCancelUserCreation(true);
      return;
    }
    handleReset();
  };

  const handleNext = async () => {
    const errors = await formikAddUser.validateForm();
    if (Object.keys(errors).length !== 0) {
      formikAddUser.setTouched(setNestedObjectValues(errors, true));
      return;
    }
    setStep((s) => s + 1);
  };

  const footer =
    step === 0 ? (
      <>
        <Button variant="default-outlined" onClick={handleClose}>
          {t("Cancel")}
        </Button>
        <Button
          variant="primary-filled"
          disabled={!formikAddUser.values.companyId}
          onClick={handleNext}
        >
          {t("Next")}
        </Button>
      </>
    ) : (
      <>
        <Button variant="default-outlined" onClick={handleClose}>
          {t("Cancel")}
        </Button>
        <Button variant="primary-soft" onClick={() => setStep((s) => s - 1)}>
          {t("Back")}
        </Button>
        <Button
          variant="primary-filled"
          onClick={() => formikAddUser.submitForm()}
        >
          {t("Create")}
        </Button>
      </>
    );

  return (
    <>
      <Drawer
        open={open}
        onClose={handleClose}
        title={t("Add User")}
        loading={loading}
        footer={footer}
      >
        {step === 0 && (
          <PlatformUserInformation
            formik={formikAddUser}
            isNew={true}
            customFieldSettings={customFieldSettings}
          />
        )}
        {step === 1 && (
          <Stack gap="var(--space-8)">
            <PlatformDisplayUserInformation
              handleClickEdit={() => setStep(0)}
              formik={formikAddUser}
            />
            <FormControlLabel
              checked={formikAddUser.values.sendOneTimePassword}
              control={
                <Checkbox
                  value={formikAddUser.values.sendOneTimePassword}
                  name="sendOneTimePassword"
                  onChange={formikAddUser.handleChange}
                />
              }
              label={
                <Typography variant="label" component="span">
                  {t(
                    "Send one-time password and notify new employee of their invitation to",
                  )}
                  <img
                    src={logo}
                    alt="Company Logo"
                    style={{
                      height: 16,
                      width: "auto",
                      marginLeft: 8,
                      marginBottom: 2,
                      display: "inline-block",
                      verticalAlign: "middle",
                    }}
                    draggable={false}
                  />
                </Typography>
              }
            />
          </Stack>
        )}
      </Drawer>
      <ConfirmDialog
        open={openCancelUserCreation}
        title={t("Cancel User Creation")}
        tone="primary"
        confirmLabel={t("Yes")}
        cancelLabel={t("No")}
        message={
          <Trans
            i18nKey="CancelUserCreationDescription"
            components={{
              canceling_text: (
                <Typography
                  component="span"
                  variant="inherit"
                  sx={{ fontWeight: "var(--type-weight-medium)" }}
                />
              ),
            }}
          />
        }
        onCancel={handleCloseCancelUserCreationModal}
        onConfirm={() => {
          handleReset();
          handleCloseCancelUserCreationModal();
        }}
      />
    </>
  );
};

export default PlatformAddNewUserDrawer;
